import { useEffect } from 'react'
import { PREFIX_LANGS, getPathLang } from './lang'

const SITE = 'https://tools.seo-solution.cz'

/** Všechny jazyky (cs = bez prefixu). Pořadí drž shodné se sitemapou. */
const ALL_LANGS = ['cs', ...PREFIX_LANGS] as const

/** og:locale podle jazyka. */
const OG_LOCALE: Record<string, string> = {
  cs: 'cs_CZ', en: 'en_US', de: 'de_DE', pl: 'pl_PL',
  sk: 'sk_SK', it: 'it_IT', hu: 'hu_HU', uk: 'uk_UA',
}

interface SeoOpts {
  /** <title>. Když chybí, ponechá se stávající (např. kurátorovaný z index.html). */
  title?: string
  /** Meta description. Když chybí, ponechá se stávající. */
  description?: string
  /**
   * Cesta pro canonical/hreflang BEZ jazykového prefixu, např. '/napoveda/canonical'.
   * Když chybí, odvodí se z aktuální URL (po odstranění jazykového prefixu).
   */
  canonicalPath?: string
  /** JSON-LD strukturovaná data (objekt nebo pole objektů). */
  jsonLd?: object | object[]
  image?: string
  /** Meta keywords (Google ignoruje, ale Seznam.cz může zohlednit). Čárkami oddělené. */
  keywords?: string
  /**
   * Stránka existuje ve všech 12 jazycích → vygeneruj hreflang alternates + self-canonical
   * per jazyk. Default true (všechny veřejné routy obsluhují i jazykové prefixy).
   */
  localized?: boolean
  /**
   * Lokalizovaná cesta (segment + slug) per jazyk — pro stránky s přeloženými URL.
   * Vrací cestu BEZ jazykového prefixu pro daný jazyk (např. lang 'de' → '/werkzeuge/robots-txt-prufen').
   * Když je zadáno, canonical i hreflang se generují z těchto cest místo jednotného canonicalPath.
   */
  hreflangPath?: (lang: string) => string
}

function upsertMeta(key: 'name' | 'property', val: string, content: string) {
  let el = document.head.querySelector(`meta[${key}="${val}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(key, val)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Sestaví absolutní URL pro daný jazyk + jazykově neutrální cestu (stejná logika jako gen-sitemap.mjs). */
function localizedUrl(lang: string, basePath: string): string {
  const prefix = lang === 'cs' ? '' : `/${lang}`
  const p = prefix + (basePath === '/' ? '' : basePath)
  return SITE + (p === '' ? '/' : p)
}

/** Odstraní vedoucí jazykový prefix (/en, /de…) z pathname → jazykově neutrální cesta. */
function stripLangPrefix(pathname: string): string {
  const parts = pathname.split('/')
  if ((PREFIX_LANGS as readonly string[]).includes(parts[1])) {
    parts.splice(1, 1)
    const rest = parts.join('/')
    return rest === '' ? '/' : rest
  }
  return pathname || '/'
}

/**
 * Nastaví per-stránku <title>, meta description, OG tagy, canonical, hreflang alternates a JSON-LD.
 *
 * Canonical je SELF-REFERENCING per jazyk (např. /it/napoveda/x → canonical na /it/napoveda/x),
 * doplněný kompletní sadou hreflang odkazů. Bez toho Google hlásí jazykové varianty jako
 * „Alternativní stránka se správnou značkou kanonické stránky" a neindexuje je.
 *
 * Po odmountování vrátí původní title/canonical a odebere přidané hreflang/JSON-LD.
 */
export function useSeoHead({
  title, description, canonicalPath, jsonLd, image, keywords, localized = true, hreflangPath,
}: SeoOpts) {
  useEffect(() => {
    const prevTitle = document.title
    if (title) document.title = title

    if (description) {
      upsertMeta('name', 'description', description)
      upsertMeta('property', 'og:description', description)
    }
    if (title) upsertMeta('property', 'og:title', title)
    if (image) upsertMeta('property', 'og:image', image)

    const kwEl = document.head.querySelector('meta[name="keywords"]') as HTMLMetaElement | null
    const prevKeywords = kwEl?.getAttribute('content') ?? null
    if (keywords) upsertMeta('name', 'keywords', keywords)

    const lang = getPathLang() || 'cs'
    const basePath = canonicalPath ?? stripLangPrefix(window.location.pathname)
    const pathFor = (l: string) => (hreflangPath ? hreflangPath(l) : basePath)
    const url = localizedUrl(lang, pathFor(lang))

    upsertMeta('property', 'og:url', url)
    if (OG_LOCALE[lang]) upsertMeta('property', 'og:locale', OG_LOCALE[lang])

    // Canonical (self-referencing s jazykovým prefixem).
    let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    const prevCanonical = link?.getAttribute('href') ?? null
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', 'canonical')
      document.head.appendChild(link)
    }
    link.setAttribute('href', url)

    // hreflang alternates — nahraď statické (z index.html, jen pro homepage) za per-stránku správné.
    const hreflangEls: HTMLLinkElement[] = []
    if (localized) {
      document.head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((el) => el.remove())
      const addAlt = (hl: string, href: string) => {
        const el = document.createElement('link')
        el.setAttribute('rel', 'alternate')
        el.setAttribute('hreflang', hl)
        el.setAttribute('href', href)
        el.setAttribute('data-seo-head', '1')
        document.head.appendChild(el)
        hreflangEls.push(el)
      }
      for (const l of ALL_LANGS) addAlt(l, localizedUrl(l, pathFor(l)))
      addAlt('x-default', localizedUrl('cs', pathFor('cs')))
    }

    let script: HTMLScriptElement | null = null
    if (jsonLd) {
      script = document.createElement('script')
      script.type = 'application/ld+json'
      script.setAttribute('data-seo-head', '1')
      script.textContent = JSON.stringify(jsonLd)
      document.head.appendChild(script)
    }

    return () => {
      if (title) document.title = prevTitle
      if (prevCanonical) link?.setAttribute('href', prevCanonical)
      if (keywords && prevKeywords !== null) upsertMeta('name', 'keywords', prevKeywords)
      hreflangEls.forEach((el) => el.remove())
      script?.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonicalPath, image, keywords, localized, JSON.stringify(jsonLd)])
}

export { SITE }
